import { useState, useEffect, useRef, useMemo } from "react";
import { DemandSourceRecord, CountryOption, ExtendedInventoryItem, InventoryResponseDto, MessageType, ResponseStatus, User, DemandSourcesResponseDto } from "../utils/types";
import { Inventory } from "../services/Inventory";
import { PagesHelper } from "../common/PagesHelper";
import CountriesAutocomplete from "../components/autocompletes/countries/CountriesAutocomplete";
import DemandSourcesAutocomplete from "../components/autocompletes/demandSources/DemandSourcesAutocomplete";
import COUNTRIES from "../components/autocompletes/countries/countries";
import Loading from "../utils/Loding";
import { Alert, Button, Card, Col, Form, InputGroup, Row, Table, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { faQuestionCircle } from "@fortawesome/free-regular-svg-icons";
import PaginationButtons from "../datagrid/PaginationButtons";
import ValidationsWrapper from "../components/validation/ValidationsWrapper";
import { useDebounce } from "../utils/hooks/useDebounce";
import { useAbortController } from "../hooks/useAbortController";
import TimeAgo from "timeago-react";
import dayjs from "dayjs";

interface InventoryPageProps {
  currentUser: User
}
type SortField = 'demandSourceName' | 'domain' | 'countryCode' | 'trackingUrl' | 'eepc' | 'valid';

const COUNTRIES_WITH_GLOBAL: CountryOption[] = [
  { id: "GLOBAL", name: "🌍 Global" },
  ...COUNTRIES,
];

function InventoryPage (props: InventoryPageProps) {
  const [inventoryDto, setInventoryDto] = useState<InventoryResponseDto>({
    status: ResponseStatus.LOADING,
    error: null,
    inventory: null
  });
  const [selectedCountries, setSelectedCountries] = useState<CountryOption[]>([COUNTRIES_WITH_GLOBAL.find((c) => c.id === "US")]);
  const [selectedDemandSources, setSelectedDemandSources] = useState<DemandSourceRecord[]>([]);
  const [demandSourcesDto, setDemandSourcesDto] = useState<DemandSourcesResponseDto>(null);
  const [selectedDomain, setSelectedDomain] = useState("");
  const [hasSetDefaultDemandSource, setHasSetDefaultDemandSource] = useState(false);
  const [sortField, setSortField] = useState<SortField>('domain');
  const [sortAsc, setSortAsc] = useState(true);
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagedItems, setPagedItems] = useState([]);
  const [isPageRenderLoading, setIsPageRenderLoading] = useState(false);
  const [charLimit, setCharLimit] = useState(50);

  const countriesRef = useRef(null);
  const demandSorucesRef = useRef(null);
  const { createController: createDemandSourcesController } = useAbortController();
  const { createController: createInventoryController } = useAbortController();
  const debouncedSelectedDomain = useDebounce(selectedDomain, 300);
  const cellRef = useRef<HTMLTableCellElement>(null);

  const countriesOnChange = (selectedValues:any) => {
    setSelectedCountries(selectedValues);
  }

  const demandSourcesOnChange = (selectedValues:any) => {
    setSelectedDemandSources(selectedValues);
  }

  const domainOnChange = (selectedValues:any) => {
    setSelectedDomain(selectedValues);
  }

  const items = useMemo<ExtendedInventoryItem[]>(() => {
    const items = inventoryDto?.inventory ?? [];
    return items.map((item) => ({
      ...item,
      demandSourceName: PagesHelper.getDemandSourceLabel(item.demandSourceId) ?? item.demandSourceId,
    }));
  }, [inventoryDto?.inventory]);

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      } else if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortAsc ? aVal - bVal : bVal - aVal;
      } else if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
        return sortAsc ? Number(aVal) - Number(bVal) : Number(bVal) - Number(aVal);
      } else {
        return 0;
      }
    });
  }, [items, sortField, sortAsc]);

  const toggleSort = (field: SortField) => {
    if (field === sortField) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const sortIndicator = (field: SortField) => sortField === field ? (sortAsc ? ' ▲' : ' ▼') : '';

  const searchIcon = <FontAwesomeIcon icon={faSearch} fixedWidth></FontAwesomeIcon>;

  const exportToCSV = () => {
    const headers = ['Domain', 'Demand Source', 'Country', 'Tracking URL', 'eEPC', 'Valid'];
    const csvHeaders = headers.map(h => `"${h}"`).join(',');
    
    const csvRows = sortedItems.map(item => {
      const countryName = COUNTRIES_WITH_GLOBAL.find(c => c.id === item.countryCode?.toUpperCase())?.name;
      const row = [
        item.domain,
        item.demandSourceName,
        countryName,
        item.trackingUrl,
        item.eepc,
        item.valid ? 'Yes' : 'No'
      ];
      return row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',');
    });
    
    const csvContent = [csvHeaders, ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const timestamp = dayjs().format('YYYY-MM-DD_HHmmss');
    const filename = `inventory_export_${timestamp}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  let lastIndex = currentPage * pageSize;
  const initialIndex = lastIndex - pageSize;
  lastIndex = lastIndex > sortedItems.length ? sortedItems.length : lastIndex;

  const isDisabled = selectedDomain === "" && (selectedCountries.length === 0 || selectedDemandSources.length === 0);

  useEffect(() => {
    if (props.currentUser) {
      const controller = createDemandSourcesController();
      PagesHelper.loadDemandSourcesHelper(controller, setDemandSourcesDto);
    }
  }, [props.currentUser, createDemandSourcesController]);

  useEffect(() => {
    if (demandSourcesDto?.demandSourcesResponse && selectedDemandSources.length === 0 && !hasSetDefaultDemandSource) {
      const defaultDemandSource = demandSourcesDto.demandSourcesResponse.find((c) => c.demandSourceId === "DN5XUZMZMJ");
      if (defaultDemandSource) {
        setSelectedDemandSources([defaultDemandSource]);
        setHasSetDefaultDemandSource(true);
      }
    }
  }, [demandSourcesDto, selectedDemandSources.length, hasSetDefaultDemandSource]);

  useEffect(() => {
    if (!props.currentUser || !demandSourcesDto) return;
    
    // If demand sources failed to load, we can't proceed with inventory loading
    if (demandSourcesDto.status === ResponseStatus.ERROR) return;
    
    // If demand sources response is null/empty, we can't proceed
    if (!demandSourcesDto.demandSourcesResponse || demandSourcesDto.demandSourcesResponse.length === 0) return;

    if (isDisabled) {
      setInventoryDto({
        status: null,
        error: null,
        inventory: null
      });
      return;
    }

    const selectedCountryIds = selectedCountries.map(c => c.id).join(",");
    const selectedDemandSourceIds = selectedDemandSources.map(d => d.demandSourceId).join(",");

    const inventoryController = createInventoryController();
    Inventory.getInventory(selectedDemandSourceIds, selectedCountryIds.toLowerCase(), debouncedSelectedDomain, setInventoryDto, inventoryController);
  }, [
    props.currentUser,
    isDisabled,
    selectedCountries,
    selectedDemandSources,
    debouncedSelectedDomain,
    demandSourcesDto,
    createInventoryController
  ]);

  useEffect(() => {
    let isRenderActive = true;
    setIsPageRenderLoading(true);

    Promise.resolve().then(() => {
      if (!isRenderActive) return;
      setPagedItems(sortedItems.slice(initialIndex, lastIndex));
      setIsPageRenderLoading(false);
    });

    return () => {
      isRenderActive = false;
    };
  }, [sortedItems, initialIndex, lastIndex]);

  useEffect(() => {
    if (sortedItems.length === 0 && currentPage > 1) {
      setCurrentPage(1);
    }
  }, [sortedItems.length, currentPage]);

  useEffect(() => {
    function updateLimit() {
      if (cellRef.current) {
        const width = cellRef.current.offsetWidth;
        // Adjust 8 here to tweak chars per pixel
        setCharLimit(Math.floor(width / 8));
      }
    }

    updateLimit();
    window.addEventListener("resize", updateLimit);
    return () => window.removeEventListener("resize", updateLimit);
  }, []);

  if (!demandSourcesDto) {
    return (
      <Loading />
    );
  }

  if (demandSourcesDto.status === ResponseStatus.ERROR) {
    return (
      <Alert variant="danger">
        <h4>Unable to load demand sources</h4>
        <p>Error: {demandSourcesDto.error}</p>
        <p>This page requires demand sources data to function. Please check your API configuration.</p>
      </Alert>
    );
  }

  if (inventoryDto.status === ResponseStatus.ERROR) {
    return (
      <>
        <br />
        <Row>
          <Col sm={3} className="text-right"></Col>
          <Col sm={6} className="text-center">
            <Alert variant={MessageType.DANGER} className="text-center border border-danger">
              <div>{inventoryDto?.error ? inventoryDto.error : "Unable to load results from the server."}</div>
              <div>Queried: <TimeAgo datetime={ dayjs.utc().toDate()} locale='en_US'/></div>
            </Alert>
          </Col>
          <Col sm={3} className="text-right"></Col>
        </Row>
      </>
    );
  }

  return (
    <>
      <br />
      <Row>
        <Col xs={12} sm={6} md={4} lg={3} className="text-center mb-2">
          <Card style={{border: "none"}}>
            <Card.Title style={{ fontSize: "100%"}}>Domain search</Card.Title>
            <InputGroup size="sm">
              <Form.Control
                type="text"
                title="Partial domains are allowed"
                as="input"
                size="sm"
                placeholder="Domain"
                onChange={(e) => domainOnChange(e.target.value)}
                value={selectedDomain}
                style={{ fontSize: 11 }}
              />
              <InputGroup.Text style={{ fontSize: 11 }}>
                {searchIcon}
              </InputGroup.Text>
            </InputGroup>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={4} lg={3} className="text-center mb-2">
          <Card style={{border: "none"}}>
            <Card.Title style={{ fontSize: "100%"}}>
              Demand Source{" "}
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="demand-source-tooltip">
                    Demand Source - Select the demand source providing the inventory items.
                  </Tooltip>
                }
              >
                <FontAwesomeIcon icon={faQuestionCircle} style={{ fontSize: "14px", color: "#9fcdff" }} />
              </OverlayTrigger>
            </Card.Title>
            <ValidationsWrapper
              name={"demandSources"}
              required={selectedDomain === ""}
              isAutocomplete
              defaultDirty
              value={selectedDemandSources}
            >
              <DemandSourcesAutocomplete
                onChange={(e : any) => demandSourcesOnChange(e)}
                options={(demandSourcesDto?.demandSourcesResponse || [])
                  .filter((c) => c.demandSourceName)
                  .sort((a, b) => {
                    const labelA = a.demandSourcePartnerName + " - " + a.demandSourceName;
                    const labelB = b.demandSourcePartnerName + " - " + b.demandSourceName;
                    return labelA.localeCompare(labelB);
                  })
                }
                selected={selectedDemandSources}
                reactRef={demandSorucesRef}
              />
            </ValidationsWrapper>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={4} lg={3} className="text-center mb-2">
          <Card style={{border: "none"}}>
            <Card.Title style={{ fontSize: "100%"}}>
              Country{" "}
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="country-tooltip">
                    Country - Filter inventory by geographic location. The country list is predefined and not dependent on the selected demand source.
                  </Tooltip>
                }
              >
                <FontAwesomeIcon icon={faQuestionCircle} style={{ fontSize: "14px", color: "#9fcdff" }} />
              </OverlayTrigger>
            </Card.Title>
            <ValidationsWrapper
              name={"countries"}
              required={selectedDomain === ""}
              isAutocomplete
              defaultDirty
              value={selectedCountries}
            >
              <CountriesAutocomplete
                onChange={(e : any) => countriesOnChange(e)}
                options={COUNTRIES_WITH_GLOBAL}
                selected={selectedCountries}
                reactRef={countriesRef}
              />
            </ValidationsWrapper>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={12} lg={2} className="text-center mb-2">
          <Card style={{border: "none"}}>
            <Card.Title style={{ fontSize: "100%" }}>&nbsp;</Card.Title>
            <Button 
              style={{ fontSize: 11, width: "120px" }}
              size="sm"
              variant="outline-primary"
              onClick={exportToCSV}
              disabled={sortedItems.length === 0}
            >
              Export as CSV
            </Button>
          </Card>
        </Col>

      </Row>

      {
        inventoryDto?.status === ResponseStatus.LOADING ? (
          <Loading />
        ) : (
          <div>
            <Table striped bordered hover>
              <thead style={{ position: "sticky", top: 0, background: "white", zIndex: 1 }}>
                <tr>
                  <th onClick={() => toggleSort('domain')} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    Domain{sortIndicator('domain')}
                  </th>
                  <th onClick={() => toggleSort('demandSourceName')} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    Demand Source{sortIndicator('demandSourceName')}
                  </th>
                  <th onClick={() => toggleSort('countryCode')} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    Country{sortIndicator('countryCode')}
                  </th>
                  <th onClick={() => toggleSort('trackingUrl')} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    Tracking URL{sortIndicator('trackingUrl')}
                  </th>
                  <th onClick={() => toggleSort('eepc')} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    eEPC{sortIndicator('eepc')}
                  </th>
                  <th onClick={() => toggleSort('valid')} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    Valid{sortIndicator('valid')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {
                  isPageRenderLoading || inventoryDto?.status === (ResponseStatus.LOADING as ResponseStatus) ? (
                    <tr>
                      <td colSpan={6} className="text-center">
                        <Loading />
                      </td>
                    </tr>
                  ) : (
                    pagedItems.map(item => (
                      <tr key={`${item.domain}-${item.demandSourceName}-${item.country}`}>
                        <td>{item.domain}</td>
                        <td>{item.demandSourceName}</td>
                        <td>{COUNTRIES_WITH_GLOBAL.find(c => c.id === item.countryCode?.toUpperCase())?.name}</td>
                        <td ref={cellRef} title={item.trackingUrl}>
                          <a href={item.trackingUrl} target="_blank" rel="noopener noreferrer">
                            {item.trackingUrl.length > charLimit ? item.trackingUrl.slice(0, charLimit) + "..." : item.trackingUrl}
                          </a>
                        </td>
                        <td>{item.eepc}</td>
                        <td>{item.valid ? '✅' : '❌'}</td>
                      </tr>
                    ))
                  )
                }
              </tbody>
            </Table>
            {inventoryDto.status !== null && (
              <PaginationButtons
                size={sortedItems.length}
                pageSize={pageSize}
                setPageSize={setPageSize}
                currentPage={currentPage} 
                setCurrentPage={setCurrentPage}
              />
            )}
          </div>
        )
      }
    </>
  );
}

export default InventoryPage;